import type {
  OpencodeMessagePart,
  OpencodeTextPart,
  OpencodeToolPart,
  SessionMessageWithParts,
} from "schemas/generated/agentTools/opencode";
import type { AgentToolModel } from "./agent-tool";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

import { getObjectDetail } from "../display/shared";
import { getAgentToolModels } from "./agent-tool";

export const TASK_SECTION_HEADER_METADATA_CONTENT_TYPE = "taskSectionHeader";

// File-editing tools that should classify as "editing"/"edit" activity. Covers
// the classic write tools plus the Codex/opencode patch family (apply_patch,
// patch, multiedit), whose payloads are diffs rather than a single filePath.
const EDIT_FAMILY_TOOL_NAMES = new Set([
  "apply_patch",
  "edit",
  "multiedit",
  "patch",
  "write",
]);

const taskSectionHeaderMetadataSchema = z.object({
  reploContentType: z.literal(TASK_SECTION_HEADER_METADATA_CONTENT_TYPE),
  sectionStartCallId: z.string().min(1),
  streamingText: z.string().min(1),
  completedText: z.string().min(1),
});

export type AgentSessionActivityPhaseHeader = Omit<
  z.infer<typeof taskSectionHeaderMetadataSchema>,
  "reploContentType"
>;

type AgentSessionTextPart = {
  kind: "text";
  id: string;
  source: OpencodeMessagePart & { partData: OpencodeTextPart };
  text: string;
  synthetic: boolean;
};

type AgentSessionPart =
  | AgentSessionTextPart
  | {
      kind: "tool";
      id: string;
      source: OpencodeMessagePart & { partData: OpencodeToolPart };
      tool: AgentToolModel;
    }
  | { kind: "file"; id: string; source: OpencodeMessagePart }
  | { kind: "other"; id: string; source: OpencodeMessagePart };

type AgentSessionMessage = {
  parts: readonly AgentSessionPart[];
};

export type AgentSessionTextItem = {
  type: "text";
  id: string;
  text: string;
};

export type AgentSessionChainItem =
  | AgentSessionTextItem
  | {
      type: "tool";
      tool: AgentToolModel;
    };

type AgentSessionActivityStatus = AgentToolModel["status"];

type AgentSessionActivityGroupBase = {
  id: string;
  status: AgentSessionActivityStatus;
  items: readonly AgentSessionChainItem[];
};

export type AgentSessionActivityGroup =
  | (AgentSessionActivityGroupBase & {
      type: "searching";
      queries: readonly { id: string; text: string; sourceLabel?: string }[];
    })
  | (AgentSessionActivityGroupBase & {
      type: "editing";
      files: readonly {
        id: string;
        path: string;
        action: "editing" | "uploading" | "writing";
      }[];
    })
  | (AgentSessionActivityGroupBase & {
      type: "reading";
      files: readonly { id: string; path: string }[];
    })
  | (AgentSessionActivityGroupBase & {
      type: "referencing";
      sources: readonly { id: string; label: string }[];
    })
  | (AgentSessionActivityGroupBase & {
      type: "running";
      commands: readonly { id: string; label: string }[];
    })
  | (AgentSessionActivityGroupBase & {
      type: "generic";
      label: string;
    });

export type AgentSessionActivityPhase = {
  type: "activityPhase";
  id: string;
  status: AgentSessionActivityStatus;
  summary: {
    streamingText: string;
    completedText: string;
    source: "fallback" | "generated";
  };
  groups: readonly AgentSessionActivityGroup[];
  items: readonly AgentSessionChainItem[];
};

export type AgentSessionItem =
  | AgentSessionTextItem
  | {
      type: "chain";
      items: readonly AgentSessionChainItem[];
      phases: readonly AgentSessionActivityPhase[];
    }
  | {
      type: "standaloneTool";
      tool: AgentToolModel;
    };

export type AgentSessionModel = {
  allParts: readonly OpencodeMessagePart[];
  isCancelled: boolean;
  items: readonly AgentSessionItem[];
  lastMessage: SessionMessageWithParts | null;
  messages: readonly AgentSessionMessage[];
  tools: readonly AgentToolModel[];
  visibleParts: readonly OpencodeMessagePart[];
};

export function getActivityPhaseHeaderFromTextPart({
  part,
}: {
  part: OpencodeTextPart;
}): AgentSessionActivityPhaseHeader | null {
  const parsedHeader = taskSectionHeaderMetadataSchema.safeParse(part.metadata);
  if (!parsedHeader.success) {
    return null;
  }

  const { sectionStartCallId, streamingText, completedText } =
    parsedHeader.data;
  return { sectionStartCallId, streamingText, completedText };
}

type AgentSessionActivityKind =
  | "editing"
  | "generic"
  | "reading"
  | "referencing"
  | "running"
  | "searching";

export type AgentSessionActivityPhaseKind =
  | "ask"
  | "delegate"
  | "edit"
  | "other"
  | "plan"
  | "research"
  | "run";

function getToolActivityKind({
  tool,
}: {
  tool: AgentToolModel;
}): AgentSessionActivityKind {
  // (Jasmine, 2026-07-02, REPL-28724): Classify by tool name before falling
  // back to input-shape checks. Tool inputs stream in incrementally, so
  // input-based classification flips mid-stream (e.g. generic -> reading once
  // filePath arrives), which re-keys activity groups and makes headers pop.
  const toolName = tool.toolName.toLowerCase();
  if (toolName === "grep" || toolName === "glob") {
    return "searching";
  }
  if (toolName === "webfetch" || toolName === "browser_navigate") {
    return "referencing";
  }
  if (toolName.includes("read") || toolName === "view") {
    return extractExternalSource({ tool }) ? "referencing" : "reading";
  }
  if (EDIT_FAMILY_TOOL_NAMES.has(toolName)) {
    return "editing";
  }
  if (toolName === "bash") {
    return "running";
  }
  if (extractSearchQuery({ tool })) {
    return "searching";
  }
  if (extractExternalSource({ tool })) {
    return "referencing";
  }
  if (extractEditedFile({ tool })) {
    return "editing";
  }
  return "generic";
}

export function getToolActivityPhaseKind({
  tool,
}: {
  tool: AgentToolModel;
}): AgentSessionActivityPhaseKind {
  if (tool.content.kind === "question") {
    return "ask";
  }
  if (tool.content.kind === "todoWrite") {
    return "plan";
  }
  if (tool.content.kind === "subagentTask") {
    return "delegate";
  }

  const toolName = tool.toolName.toLowerCase();
  if (
    toolName === "grep" ||
    toolName === "glob" ||
    toolName === "webfetch" ||
    toolName === "view" ||
    toolName.includes("read") ||
    toolName.includes("search") ||
    toolName.includes("find") ||
    toolName.includes("list")
  ) {
    return "research";
  }
  if (
    EDIT_FAMILY_TOOL_NAMES.has(toolName) ||
    toolName.includes("upload") ||
    toolName.includes("asset") ||
    toolName.includes("file")
  ) {
    return "edit";
  }
  if (toolName === "bash") {
    return "run";
  }

  return "other";
}

function getActivityGroupStatus({
  items,
}: {
  items: readonly AgentSessionChainItem[];
}): AgentSessionActivityStatus {
  const tools = items.flatMap((item) => {
    return item.type === "tool" ? [item.tool] : [];
  });
  if (
    tools.some((tool) => tool.status === "pending" || tool.status === "running")
  ) {
    return "running";
  }
  if (tools.some((tool) => tool.status === "error")) {
    return "error";
  }
  return tools[0]?.status ?? "completed";
}

function getGenericGroupLabel({
  items,
}: {
  items: readonly AgentSessionChainItem[];
}) {
  const firstTool = items.find((item) => item.type === "tool")?.tool;
  return (
    firstTool?.display.header ??
    firstTool?.display.summary ??
    firstTool?.toolName ??
    "Working"
  );
}

function extractSearchQuery({ tool }: { tool: AgentToolModel }) {
  return getObjectDetail({
    properties: ["search", "searchTerm", "query", "pattern"],
    value: tool.debug.input,
  });
}

function extractEditedFile({
  tool,
}: {
  tool: AgentToolModel;
}): { action: "editing" | "uploading" | "writing"; path: string } | null {
  const path = getObjectDetail({
    properties: ["filePath", "file_path", "path"],
    value: tool.debug.input,
  });
  if (!path) {
    return null;
  }

  const toolName = tool.toolName.toLowerCase();
  if (toolName.includes("upload")) {
    return { action: "uploading", path };
  }
  if (toolName === "write") {
    return { action: "writing", path };
  }
  if (toolName === "edit" || toolName.includes("file")) {
    return { action: "editing", path };
  }
  return null;
}

function extractReadFile({ tool }: { tool: AgentToolModel }) {
  const toolName = tool.toolName.toLowerCase();
  if (!toolName.includes("read") && toolName !== "view") {
    return null;
  }

  const path = getObjectDetail({
    properties: ["filePath", "file_path", "path"],
    value: tool.debug.input,
  });
  return path && !isExternalSourceValue({ value: path }) ? path : null;
}

function extractExternalSource({ tool }: { tool: AgentToolModel }) {
  const toolName = tool.toolName.toLowerCase();
  if (
    toolName !== "webfetch" &&
    toolName !== "read" &&
    toolName !== "browser_navigate"
  ) {
    return null;
  }

  const source = getObjectDetail({
    properties: ["url", "href", "source", "filePath", "file_path", "path"],
    value: tool.debug.input,
  });
  if (!source || !isExternalSourceValue({ value: source })) {
    return null;
  }

  return getExternalSourceLabel({ value: source });
}

function extractCommandLabel({ tool }: { tool: AgentToolModel }) {
  return getObjectDetail({
    properties: ["description", "command"],
    value: tool.debug.input,
  });
}

function isExternalSourceValue({ value }: { value: string }) {
  return /^(?:https?:\/\/|www\.)/i.test(value.trim());
}

function getExternalSourceLabel({ value }: { value: string }) {
  const trimmedValue = value.trim();
  try {
    const url = new URL(
      trimmedValue.startsWith("www.")
        ? `https://${trimmedValue}`
        : trimmedValue,
    );
    return url.hostname.replace(/^www\./, "");
  } catch {
    return trimmedValue.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  }
}

const DEDICATED_ACTIVITY_GROUP_TOOL_KINDS = new Set([
  "question",
  "todoWrite",
  "subagentTask",
]);

function startsDedicatedActivityGroup({ tool }: { tool: AgentToolModel }) {
  return DEDICATED_ACTIVITY_GROUP_TOOL_KINDS.has(tool.content.kind);
}

function buildActivityGroup({
  activityKind,
  id,
  items,
}: {
  activityKind: AgentSessionActivityKind;
  id: string;
  items: readonly AgentSessionChainItem[];
}): AgentSessionActivityGroup {
  const status = getActivityGroupStatus({ items });
  const base = { id, status, items };

  return exhaustiveSwitch({ type: activityKind })<AgentSessionActivityGroup>({
    searching: () => ({
      ...base,
      type: "searching",
      queries: mapToolActivityItems({
        items,
        getValue: (tool) => {
          const query = extractSearchQuery({ tool });
          return query ? { text: query } : null;
        },
      }),
    }),
    editing: () => ({
      ...base,
      type: "editing",
      files: mapToolActivityItems({
        items,
        getValue: (tool) => extractEditedFile({ tool }),
      }),
    }),
    reading: () => ({
      ...base,
      type: "reading",
      files: mapToolActivityItems({
        items,
        getValue: (tool) => {
          const path = extractReadFile({ tool });
          return path ? { path } : null;
        },
      }),
    }),
    referencing: () => ({
      ...base,
      type: "referencing",
      sources: mapToolActivityItems({
        items,
        getValue: (tool) => {
          const label = extractExternalSource({ tool });
          return label ? { label } : null;
        },
      }),
    }),
    running: () => ({
      ...base,
      type: "running",
      commands: mapToolActivityItems({
        items,
        getValue: (tool) => {
          const label = extractCommandLabel({ tool });
          return label ? { label } : null;
        },
      }),
    }),
    generic: () => ({
      ...base,
      type: "generic",
      label: getGenericGroupLabel({ items }),
    }),
  });
}

function mapToolActivityItems<ActivityDetailType extends object>({
  getValue,
  items,
}: {
  getValue: (tool: AgentToolModel) => ActivityDetailType | null;
  items: readonly AgentSessionChainItem[];
}): ({ id: string } & ActivityDetailType)[] {
  return items.flatMap((item) => {
    if (item.type !== "tool") {
      return [];
    }

    const value = getValue(item.tool);
    return value ? [{ id: item.tool.callId, ...value }] : [];
  });
}

function buildActivityGroups({
  items,
}: {
  items: readonly AgentSessionChainItem[];
}): AgentSessionActivityGroup[] {
  // (Jasmine, 2026-07-02, REPL-28724): One group per activity kind per phase,
  // keyed by the first tool of that kind. Splitting on every kind change made
  // interleaved tools (read, edit, read) produce duplicate same-kind groups
  // whose React keys churned during streaming, so headers popped in and out.
  type PendingActivityGroup = {
    activityKind: AgentSessionActivityKind;
    id: string;
    items: AgentSessionChainItem[];
  };
  const orderedGroups: PendingActivityGroup[] = [];
  const groupsByKind = new Map<
    AgentSessionActivityKind,
    PendingActivityGroup
  >();
  let lastGroup: PendingActivityGroup | null = null;

  for (const item of items) {
    if (item.type === "text") {
      lastGroup?.items.push(item);
      continue;
    }

    const activityKind = getToolActivityKind({ tool: item.tool });
    const isDedicated = startsDedicatedActivityGroup({ tool: item.tool });
    let group = isDedicated ? null : groupsByKind.get(activityKind) ?? null;
    if (!group) {
      group = { activityKind, id: item.tool.callId, items: [] };
      orderedGroups.push(group);
      if (!isDedicated) {
        groupsByKind.set(activityKind, group);
      }
    }
    group.items.push(item);
    lastGroup = group;
  }

  return orderedGroups.map((group) => buildActivityGroup(group));
}

function buildActivityPhase({
  header,
  id,
  items,
}: {
  header: AgentSessionActivityPhaseHeader | null;
  id?: string;
  items: readonly AgentSessionChainItem[];
}): AgentSessionActivityPhase | null {
  const groups = buildActivityGroups({ items });
  if (groups.length === 0) {
    return null;
  }

  const firstTool = items.find((item) => item.type === "tool")?.tool;
  const phaseId = id ?? firstTool?.callId ?? "activity-phase";
  const summary = getActivityPhaseSummary({ groups, header, id: phaseId });
  return {
    type: "activityPhase",
    id: phaseId,
    status: getActivityGroupStatus({ items }),
    summary,
    groups,
    items,
  };
}

export function buildActivityPhases({
  headersByCallId,
  items,
}: {
  headersByCallId: ReadonlyMap<string, AgentSessionActivityPhaseHeader>;
  items: readonly AgentSessionChainItem[];
}): AgentSessionActivityPhase[] {
  const phases: AgentSessionActivityPhase[] = [];
  let currentPhase: {
    header: AgentSessionActivityPhaseHeader | null;
    id: string;
    items: AgentSessionChainItem[];
    lastToolPartCallId: string | undefined;
    phaseKind: AgentSessionActivityPhaseKind;
  } | null = null;

  function flushCurrentPhase() {
    if (!currentPhase || currentPhase.items.length === 0) {
      currentPhase = null;
      return;
    }

    const phase = buildActivityPhase({
      header: currentPhase.header,
      id: currentPhase.id,
      items: currentPhase.items,
    });
    if (phase) {
      phases.push(phase);
    }
    currentPhase = null;
  }

  for (const item of items) {
    if (item.type === "text") {
      currentPhase?.items.push(item);
      continue;
    }

    const nextPhaseKind = getToolActivityPhaseKind({ tool: item.tool });
    const header = headersByCallId.get(item.tool.callId) ?? null;
    // Rows exploded from one code-mode script share partCallId: one action
    // burst, so a kind change alone never splits them into separate phases.
    const sharesPartWithCurrentPhase =
      item.tool.partCallId !== undefined &&
      item.tool.partCallId === currentPhase?.lastToolPartCallId;
    const shouldStartPhase =
      !currentPhase ||
      (currentPhase.phaseKind !== nextPhaseKind &&
        !sharesPartWithCurrentPhase) ||
      Boolean(header) ||
      startsDedicatedActivityGroup({ tool: item.tool });

    if (shouldStartPhase) {
      flushCurrentPhase();
      currentPhase = {
        header,
        id: item.tool.callId,
        items: [],
        lastToolPartCallId: undefined,
        phaseKind: nextPhaseKind,
      };
    }

    if (currentPhase) {
      currentPhase.items.push(item);
      currentPhase.lastToolPartCallId = item.tool.partCallId;
    }
  }

  flushCurrentPhase();
  return phases;
}

function getActivityPhaseSummary({
  groups,
  header,
  id,
}: {
  groups: readonly AgentSessionActivityGroup[];
  header: AgentSessionActivityPhaseHeader | null;
  id: string;
}): AgentSessionActivityPhase["summary"] {
  if (header) {
    return {
      streamingText: header.streamingText,
      completedText: header.completedText,
      source: "generated",
    };
  }

  const searchingGroup = groups.find((group) => group.type === "searching");
  const editingGroup = groups.find((group) => group.type === "editing");
  const readingGroup = groups.find((group) => group.type === "reading");
  const referencingGroup = groups.find((group) => group.type === "referencing");
  const runningGroup = groups.find((group) => group.type === "running");

  if (searchingGroup) {
    const queries = searchingGroup.queries.map((query) => query.text);
    if (isSqlSearchActivity({ queries })) {
      return {
        streamingText: "Querying analytics data",
        completedText: "Queried analytics data",
        source: "fallback",
      };
    }

    const querySummary = summarizeSearchActivity({ queries });
    return {
      streamingText: `Gathering ${querySummary ?? "references"}`,
      completedText: `Gathered ${querySummary ?? "references"}`,
      source: "fallback",
    };
  }

  if (editingGroup) {
    return {
      streamingText: "Updating project files",
      completedText: "Updated project files",
      source: "fallback",
    };
  }

  if (readingGroup) {
    return {
      streamingText: "Reviewing project files",
      completedText: "Reviewed project files",
      source: "fallback",
    };
  }

  if (referencingGroup) {
    return {
      streamingText: "Referencing external sources",
      completedText: "Referenced external sources",
      source: "fallback",
    };
  }

  if (runningGroup) {
    const commandSummary = summarizeActivityObjects({
      quoteValues: false,
      values: runningGroup.commands.map((command) => command.label),
    });
    const target = commandSummary ? ` for ${commandSummary}` : "";
    return {
      streamingText: `Running checks${target}`,
      completedText: `Ran checks${target}`,
      source: "fallback",
    };
  }

  const fallbackSummary = getGenericFallbackPhaseSummary({ id });
  return {
    streamingText: fallbackSummary.streamingText,
    completedText: fallbackSummary.completedText,
    source: "fallback",
  };
}

const GENERIC_FALLBACK_PHASE_SUMMARIES: [
  { streamingText: string; completedText: string },
  ...{ streamingText: string; completedText: string }[],
] = [
  {
    streamingText: "Working through request",
    completedText: "Worked through request",
  },
  {
    streamingText: "Coordinating task progress",
    completedText: "Coordinated task progress",
  },
  {
    streamingText: "Handling request details",
    completedText: "Handled request details",
  },
  {
    streamingText: "Continuing task workflow",
    completedText: "Continued task workflow",
  },
];

function getGenericFallbackPhaseSummary({ id }: { id: string }) {
  const hash = Array.from(id).reduce((value, character) => {
    return value + character.charCodeAt(0);
  }, 0);
  return (
    GENERIC_FALLBACK_PHASE_SUMMARIES[
      hash % GENERIC_FALLBACK_PHASE_SUMMARIES.length
    ] ?? GENERIC_FALLBACK_PHASE_SUMMARIES[0]
  );
}

function isSqlSearchActivity({ queries }: { queries: readonly string[] }) {
  return queries.some((query) => {
    return /^\s*(select|with|show|describe|explain)\b/i.test(query);
  });
}

function summarizeSearchActivity({ queries }: { queries: readonly string[] }) {
  const uniqueQueries = getUniqueTrimmedValues({ values: queries });
  if (uniqueQueries.length === 0) {
    return null;
  }
  if (uniqueQueries.length === 1) {
    return `references for "${uniqueQueries[0]}"`;
  }

  const subject = getSharedSearchSubject({ queries: uniqueQueries });
  return subject ? `references for ${subject}` : "related references";
}

function getSharedSearchSubject({ queries }: { queries: readonly string[] }) {
  const tokenizedQueries = queries.map((query) => {
    return tokenizeSearchQuery({ query });
  });
  const [firstQueryTokens] = tokenizedQueries;
  if (!firstQueryTokens) {
    return null;
  }

  const sharedToken = firstQueryTokens.find((token) => {
    return (
      token.normalized.length > 2 &&
      tokenizedQueries.every((queryTokens) => {
        return queryTokens.some((queryToken) => {
          return queryToken.normalized === token.normalized;
        });
      })
    );
  });

  return sharedToken?.display ?? null;
}

function tokenizeSearchQuery({ query }: { query: string }) {
  return query.split(/[^A-Za-z0-9]+/).flatMap((token) => {
    const trimmedToken = token.trim();
    return trimmedToken
      ? [{ display: trimmedToken, normalized: trimmedToken.toLowerCase() }]
      : [];
  });
}

function summarizeActivityObjects({
  quoteValues = true,
  values,
}: {
  quoteValues?: boolean;
  values: readonly string[];
}) {
  const uniqueValues = getUniqueTrimmedValues({ values });
  if (uniqueValues.length === 0) {
    return null;
  }
  if (uniqueValues.length === 1) {
    const [firstValue] = uniqueValues;
    return quoteValues ? `"${firstValue}"` : firstValue;
  }

  const visibleValues = uniqueValues.slice(0, 3);
  const summarizedValues = quoteValues
    ? visibleValues.map((value) => `"${value}"`)
    : visibleValues;
  const hiddenCount = uniqueValues.length - visibleValues.length;
  const joinedValues = formatNaturalList({ values: summarizedValues });

  return hiddenCount > 0
    ? `${joinedValues}, plus ${hiddenCount} more`
    : joinedValues;
}

function getUniqueTrimmedValues({ values }: { values: readonly string[] }) {
  const uniqueValues: string[] = [];
  const seenValues = new Set<string>();
  for (const value of values) {
    const trimmedValue = value.trim();
    if (!trimmedValue || seenValues.has(trimmedValue)) {
      continue;
    }
    seenValues.add(trimmedValue);
    uniqueValues.push(trimmedValue);
  }
  return uniqueValues;
}

function formatNaturalList({ values }: { values: readonly string[] }) {
  if (values.length === 0) {
    return "";
  }
  if (values.length === 1) {
    return values[0] ?? "";
  }
  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  }
  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
}

export function getLastAgentSessionChainTextItem({
  items,
}: {
  items: readonly AgentSessionItem[];
}): AgentSessionTextItem | null {
  let lastTextItem: AgentSessionTextItem | null = null;
  let isLastTextItemInChain = false;

  for (const item of items) {
    exhaustiveSwitch(item)({
      text: (textItem) => {
        lastTextItem = textItem;
        isLastTextItemInChain = false;
      },
      chain: (chainItem) => {
        for (const chainItemChild of chainItem.items) {
          if (chainItemChild.type === "text") {
            lastTextItem = chainItemChild;
            isLastTextItemInChain = true;
          }
        }
      },
      standaloneTool: () => {
        return;
      },
    });
  }

  return isLastTextItemInChain ? lastTextItem : null;
}

export function getLastAgentSessionChainText({
  items,
}: {
  items: readonly AgentSessionItem[];
}): string | null {
  let lastChainTextItems: AgentSessionTextItem[] = [];

  for (const item of items) {
    exhaustiveSwitch(item)({
      text: () => {
        lastChainTextItems = [];
      },
      chain: (chainItem) => {
        const latestToolIndex = chainItem.items.findLastIndex(
          (chainItemChild) => {
            return chainItemChild.type === "tool";
          },
        );
        lastChainTextItems = chainItem.items.flatMap(
          (chainItemChild, chainItemChildIndex) => {
            return chainItemChildIndex > latestToolIndex &&
              chainItemChild.type === "text"
              ? [chainItemChild]
              : [];
          },
        );
      },
      standaloneTool: () => {
        lastChainTextItems = [];
      },
    });
  }

  return lastChainTextItems.length > 0
    ? lastChainTextItems.map((textItem) => textItem.text).join("\n")
    : null;
}

// The single block of assistant "reply" text for a session model, used by both
// the streaming snapshot and connected-chat delivery. Chain-trailing text wins
// when the turn ended in tools; a turn that used tools but produced no closing
// text is intentionally empty; otherwise a pure-text reply is all its text.
export function getAgentSessionText({
  items,
}: {
  items: readonly AgentSessionItem[];
}): string {
  const chainText = getLastAgentSessionChainText({ items });
  if (chainText) {
    return chainText;
  }

  const hasToolItem = items.some((item) => {
    return item.type === "chain" || item.type === "standaloneTool";
  });
  if (hasToolItem) {
    return "";
  }

  return items
    .flatMap((item) => {
      return item.type === "text" ? [item.text] : [];
    })
    .join("\n");
}

function isMessageCancelled({
  messageWithParts,
}: {
  messageWithParts: SessionMessageWithParts;
}) {
  const { error } = messageWithParts.message;

  return (
    typeof error === "object" &&
    error &&
    "name" in error &&
    error.name === "MessageAbortedError"
  );
}

function hasToolAfterPart({
  sessionMessages,
  messageIndex,
  partIndex,
}: {
  sessionMessages: readonly AgentSessionMessage[];
  messageIndex: number;
  partIndex: number;
}) {
  for (
    let currentMessageIndex = messageIndex;
    currentMessageIndex < sessionMessages.length;
    currentMessageIndex += 1
  ) {
    const message = sessionMessages[currentMessageIndex];
    if (!message) {
      continue;
    }
    const startPartIndex =
      currentMessageIndex === messageIndex ? partIndex + 1 : 0;
    for (
      let currentPartIndex = startPartIndex;
      currentPartIndex < message.parts.length;
      currentPartIndex += 1
    ) {
      const nextPart = message.parts[currentPartIndex];
      // NOTE (Ben, 2026-07-03, REPL-28623): Follow-up suggestions render below the answer and should not make prior answer text look like tool narration.
      if (
        nextPart?.kind === "tool" &&
        nextPart.tool.content.kind !== "followUps"
      ) {
        return true;
      }
    }
  }

  return false;
}

function buildSessionItems({
  headersByCallId,
  isStreaming,
  sessionMessages,
}: {
  headersByCallId: ReadonlyMap<string, AgentSessionActivityPhaseHeader>;
  isStreaming: boolean;
  sessionMessages: readonly AgentSessionMessage[];
}) {
  const items: AgentSessionItem[] = [];
  let chainBuffer: AgentSessionChainItem[] = [];

  function flushChain() {
    if (chainBuffer.length > 0) {
      items.push({
        type: "chain",
        items: chainBuffer,
        phases: buildActivityPhases({ headersByCallId, items: chainBuffer }),
      });
      chainBuffer = [];
    }
  }

  for (const [messageIndex, message] of sessionMessages.entries()) {
    for (const [partIndex, part] of message.parts.entries()) {
      if (part.kind === "tool") {
        if (part.tool.content.kind === "followUps") {
          // Rendered once at the bottom of the assistant message, not inline.
          continue;
        }
        if (part.tool.content.kind === "integrationStatus") {
          flushChain();
          items.push({ type: "standaloneTool", tool: part.tool });
          continue;
        }

        chainBuffer.push({ type: "tool", tool: part.tool });
        continue;
      }

      if (
        part.kind !== "text" ||
        part.synthetic ||
        part.text.trim().length === 0
      ) {
        continue;
      }

      const textItem: AgentSessionTextItem = {
        type: "text",
        id: part.id,
        text: part.text,
      };

      if (chainBuffer.length === 0) {
        items.push(textItem);
        continue;
      }

      const hasMoreTools = isStreaming
        ? true
        : hasToolAfterPart({ sessionMessages, messageIndex, partIndex });

      if (hasMoreTools) {
        chainBuffer.push(textItem);
        continue;
      }

      flushChain();
      items.push(textItem);
    }
  }

  flushChain();
  return items;
}

export function getAgentSessionModel({
  messages,
  isStreaming = false,
}: {
  isStreaming?: boolean;
  messages: readonly SessionMessageWithParts[];
}) {
  const messageParts = messages.flatMap((message) => {
    return message.parts;
  });
  const toolContextToolParts = messageParts.flatMap((messagePart) => {
    return messagePart.partData.type === "tool" ? [messagePart.partData] : [];
  });
  const toolModelsByPartCallId = new Map<string, AgentToolModel[]>();
  for (const messagePart of messageParts) {
    if (messagePart.partData.type !== "tool") {
      continue;
    }
    toolModelsByPartCallId.set(
      messagePart.partData.callID,
      getAgentToolModels({
        currentToolPart: messagePart.partData,
        messageParts,
        sessionToolParts: toolContextToolParts,
      }),
    );
  }
  const tools = [...toolModelsByPartCallId.values()].flat();
  const headersByCallId = new Map<string, AgentSessionActivityPhaseHeader>();
  for (const messagePart of messageParts) {
    if (messagePart.partData.type !== "text") {
      continue;
    }
    const header = getActivityPhaseHeaderFromTextPart({
      part: messagePart.partData,
    });
    if (header) {
      headersByCallId.set(header.sectionStartCallId, header);
    }
  }
  const sessionMessages: AgentSessionMessage[] = messages.map((message) => {
    return {
      parts: message.parts.flatMap((part): AgentSessionPart[] => {
        if (part.partData.type === "text") {
          return [
            {
              kind: "text",
              id: part.id,
              source: { ...part, partData: part.partData },
              text: part.partData.text,
              synthetic: part.partData.synthetic ?? false,
            },
          ];
        }

        if (part.partData.type === "tool") {
          const partData = part.partData;
          const toolModels = toolModelsByPartCallId.get(partData.callID) ?? [];
          if (toolModels.length > 0) {
            return toolModels.map((tool, toolIndex) => ({
              kind: "tool",
              id: toolIndex === 0 ? part.id : `${part.id}:${toolIndex}`,
              source: { ...part, partData },
              tool,
            }));
          }
        }

        if (part.partData.type === "file") {
          return [{ kind: "file", id: part.id, source: part }];
        }

        return [{ kind: "other", id: part.id, source: part }];
      }),
    };
  });

  const visibleParts = messageParts.filter((part) => {
    return (
      part.partData.type !== "text" ||
      (!part.partData.synthetic && part.partData.text.trim().length > 0)
    );
  });

  return {
    allParts: messageParts,
    isCancelled: messages.some((messageWithParts) => {
      return isMessageCancelled({ messageWithParts });
    }),
    items: buildSessionItems({ headersByCallId, isStreaming, sessionMessages }),
    lastMessage: messages[messages.length - 1] ?? null,
    messages: sessionMessages,
    tools,
    visibleParts,
  };
}
